<template>
  <div
    v-loading="status.isLoading"
    class="page-content bill-information"
  >
    <form
      :id="formId"
      method="post"
      class="form-horizontal"
      autocomplete="off"
      @submit.prevent
    >
      <h6 class="sub-section-title">
        {{ $t('payment.billingInfo') }}
      </h6>
      <fieldset class="pb-20">
        <div class="payment-logo mt-10">
          <img
            class="lock-key"
            src="https://df9khbjwrdn51.cloudfront.net/Crystal_Project_Lock.png"
          >
          <p class="pl-10 pr-10 text-center">
            <b>
              {{ $t('payment.secureBillingAuthorizationForm') }}
            </b>
            <br>
            <span>
              {{ $t('payment.formIsSecuredSSL') }}
            </span>
          </p>
          <img
            class="secure-key"
            src="https://df9khbjwrdn51.cloudfront.net/comodo_secure_seal_100x85_transp.png"
          >
        </div>

        <div class="row">
          <label class="col-sm-3 control-label pt-10 mobile-pt-10"> {{ $t('payment.cardHolderName') }}* </label>
          <div class="col-sm-8">
            <sanitized-input
              id="holderName"
              v-model="cardHolderName"
              required
            />
            <span class="help-block">
              {{ $t('payment.fullNameOfCardHolder') }}
            </span>
          </div>
        </div>

        <stripe-elements />
      </fieldset>

      <div class="sub-section mt-20 mobile-mt-30">
        <h6 class="sub-section-title">
          {{ $t('payment.billingAddress') }}
        </h6>

        <p-button
          type="warning"
          round
          :class="{ 'ml-4': isSpanishVersion }"
          @click="onSameAsHomeAddressButtonClicked"
        >
          {{ $t('payment.sameAsHomeAddress') }}
        </p-button>
      </div>

      <expand-address-section
        v-model="billingAddress"
        :parent-id="formId"
        :show-geocode="false"
        :show-map="false"
        :show-home-note="false"
        is-editing
        required
      />

      <div class="row mt-20">
        <div class="col text-center">
          <p-button
            data-test="btn-activate-account"
            type="purple"
            native-type="submit"
            round
            :pressed="true"
            @click="onFormSubmit"
          >
            {{ $t('registration.activateGoGoAccount') }}
          </p-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { Ref, ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAddress } from '@/use/address';
import { event } from 'vue-gtag';
import util from '@/Utils/util';
import { useGtm } from '@gtm-support/vue-gtm';

import { useSwalDialog } from '@/use/swalDialog';
import { BillingAddress } from '@/models/payment';
import { BillingDetails, StripeChangeEvent, StripeIntentKind, useStripe } from '@/use/stripe';
import { getStripeErrorMessageHtml } from '@/services/payments/stripeErrors';
import { paymentService } from '@/services/payment';

const formId = 'bill-information';

export default {
  name: 'AddPaymentMethod',

  props: {
    customerInfo: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['payment-added'],

  setup: (props: any, context: any) => {
    const { emit } = context;
    const { locale } = useI18n();

    const { trimAddress } = useAddress();
    const { showErrorPopupDialog } = useSwalDialog();

    const { initStripe, terminateStripe, confirmSetup } = useStripe();

    const cardHolderName = ref('');
    const isCardComplete = ref(false);
    const isDirectExpress = ref(false);
    const billingAddress: Ref<BillingAddress> = ref({
      streetAddress: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    });
    const status = reactive({
      isLoading: true,
    });
    const gtm = useGtm();

    const isSpanishVersion = computed(() => locale.value === 'es');

    async function resetStripeElements() {
      terminateStripe();
      try {
        await initStripe({
          onChange: (events: StripeChangeEvent) => {
            isCardComplete.value = events.complete;
          },
          onReady: () => {
            status.isLoading = false;
          },
          stripeAccountName: props.customerInfo?.stripeAccountName,
          // callerId + plan let the backend decide between a SetupIntent and a single membership
          // PaymentIntent (no_preauth_retest); without them online signups always get a SetupIntent.
          callerId: props.customerInfo?.id,
          plan: props.customerInfo?.registerPlan,
        });
      } catch (error) {
        showErrorPopupDialog({
          text: `An error occurred while loading the payment form. Please try again.</br></br> ${error}`,
          type: 'html',
        });
        terminateStripe();
      }
    }

    const onFormSubmit = async () => {
      trimAddress(billingAddress.value);

      // This shouldn't be happening, but we're using onClick instead of onSubmit
      if (!isCardComplete.value || !cardHolderName.value || !billingAddress.value.streetAddress) {
        showErrorPopupDialog({
          text: 'Please complete all the information.',
        });
        return;
      }

      const billingDetails: BillingDetails = {
        name: cardHolderName.value,
        address: {
          line1: billingAddress.value.streetAddress,
          line2: billingAddress.value.streetAddress2 || '',
          city: billingAddress.value.city,
          state: billingAddress.value.state,
          postal_code: billingAddress.value.zip,
          country: billingAddress.value.country || '',
        },
      };

      try {
        status.isLoading = true;
        const { setupIntentId, error, intentKind } = await confirmSetup(billingDetails);

        if (!error) {
          await addPaymentMethod(setupIntentId, intentKind);
          return;
        }

        void paymentService.handleSetupIntentError({
          setupIntentId,
          userId: props.customerInfo.id,
        });

        showErrorPopupDialog({
          text: getStripeErrorMessageHtml(error),
          type: 'html',
        });

        resetStripeElements();
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
        await resetStripeElements();
      } finally {
        status.isLoading = false;
      }
    };

    const onSameAsHomeAddressButtonClicked = () =>
      (billingAddress.value = {
        streetAddress: props.customerInfo.streetAddress,
        streetAddress2: props.customerInfo.streetAddress2,
        city: props.customerInfo.city,
        state: props.customerInfo.state,
        zip: props.customerInfo.zip,
        country: props.customerInfo.country,
      });

    const addPaymentMethod = async (setupIntentId: string, intentKind: StripeIntentKind) => {
      try {
        await paymentService.addPaymentSource({
          setupIntentId,
          intentKind,
          userId: props.customerInfo.id,
          plan: props.customerInfo.registerPlan,
          isDirectExpress: isDirectExpress.value,
        });

        emit('payment-added', true);

        event('activation_registration_complete', {
          send_to: 'G-62RJKKDKT9',
        });

        gtm!.trackEvent({
          event: 'submit',
          category: 'web_app',
          action: 'click',
          label: 'activation_registration_complete',
        });

        window?.dataLayer?.push({ event: 'submit', category: 'web_app', action: 'click', label: 'activation_registration_complete_2' });

        event('activationRegistrationComplete', {
          event_category: 'RegistrationEvent',
          send_to: 'UA-76449918-1',
        });

        terminateStripe();
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
        await resetStripeElements();
      }
    };

    onMounted(async () => {
      await resetStripeElements();
    });

    return {
      formId,
      cardHolderName,
      billingAddress,
      status,
      isCardComplete,
      isDirectExpress,
      isSpanishVersion,

      onFormSubmit,
      onSameAsHomeAddressButtonClicked,
    };
  },
};
</script>

<style lang="scss">
.complete-registration {
  .bill-information {
    .payment-logo {
      display: flex;
      align-items: flex-start;
      justify-content: center;
      height: fit-content;
      img.lock-key {
        height: 35px;
      }
      img.secure-key {
        display: inline-block;
        position: relative;
        top: -20px;
        height: 70px;
      }
      span {
        font-size: 12px;
        @media (max-width: 576px) {
          line-height: 14px;
          display: inline-block;
        }
      }
    }
    .sub-section {
      position: relative;
      button {
        position: absolute;
        top: -14px;
        left: 120px;
        height: 30px;
        line-height: 10px;

        &.spanish-btn {
          left: 150px;
        }

        @media (max-width: 576px) {
          top: -10px;
        }
      }
    }
    .form-submit-button-group {
      .btn-unflag-account {
        margin-left: 50px;
        @media (max-width: 576px) {
          margin-left: 0;
        }
      }
      button {
        @media (max-width: 576px) {
          margin-bottom: 5px !important;
        }
      }
    }
  }
}
</style>
